package com.htmlagent.site.components.sections.landing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.htmlagent.site.components.widgets.reveal
import com.htmlagent.site.i18n.LocalStrings
import com.htmlagent.site.state.Identity
import com.htmlagent.site.theme.AccentLineStyle
import com.htmlagent.site.theme.CtaActionsStyle
import com.htmlagent.site.theme.CtaFormStyle
import com.htmlagent.site.theme.CtaInputVariant
import com.htmlagent.site.theme.CtaNoteStyle
import com.htmlagent.site.theme.CtaReturningStyle
import com.htmlagent.site.theme.CtaStyle
import com.htmlagent.site.theme.DisplayTitleStyle
import com.htmlagent.site.theme.ErrorStyle
import com.htmlagent.site.theme.LandingBandStyle
import com.htmlagent.site.theme.LandingInnerStyle
import com.htmlagent.site.theme.LandingLeadStyle
import com.htmlagent.site.theme.PillPrimaryVariant
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.TextInput
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * The last band, which is where the page stops describing and starts asking.
 *
 * It asks twice over, and the order matters: the button repeats the one thing the page has been
 * about, and the field below it is for whoever has been here before. Putting the field first
 * would turn the foot of the page into a toll on a product that has not charged anything yet.
 *
 * The check is [Identity]'s, the same as the workspace gate; the address goes to `/app`, which
 * emails it the access link.
 *
 * The `<form>` is real so Enter submits, the phone keyboard shows "go" and password managers
 * understand the field. It carries `novalidate` because with `type="email"` the browser blocks
 * the submit silently and our own message, which says what is actually wrong, never runs.
 */
@Composable
fun LandingCta(onStart: () -> Unit, onEnter: (String) -> Unit) {
    val t = LocalStrings.current
    var draft by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }

    fun submit() {
        val value = draft.trim()
        if (Identity.looksLikeEmail(value)) {
            error = null
            onEnter(value)
        } else {
            error = t.gate.invalidEmail
        }
    }

    Section(
        LandingBandStyle.toModifier().toAttrs {
            attr("id", SECTION_CTA)
            attr("aria-labelledby", headingId(SECTION_CTA))
        },
    ) {
        Div(LandingInnerStyle.toModifier().then(CtaStyle.toModifier()).toAttrs()) {
            Div(Modifier.reveal().toAttrs()) {
                H2(
                    DisplayTitleStyle.toModifier().toAttrs {
                        attr("id", headingId(SECTION_CTA))
                        attr("data-size", "hero")
                    },
                ) {
                    Text(t.landing.ctaLineOne)
                    // See the hero: the two halves need a separator for the announced name.
                    Text(" ")
                    Span(AccentLineStyle.toModifier().toAttrs()) { Text(t.landing.ctaLineTwo) }
                }

                // The three things that stop somebody, repeated where the decision is made.
                P(LandingLeadStyle.toModifier().toAttrs { attr("data-align", "center") }) {
                    Text(t.landing.ctaLead)
                }

                Div(CtaActionsStyle.toModifier().toAttrs()) {
                    Button(onClick = { onStart() }, variant = PillPrimaryVariant) {
                        Text(t.landing.heroPrimary)
                    }
                }

                P(CtaReturningStyle.toModifier().toAttrs()) { Text(t.landing.ctaReturning) }

                Form(
                    attrs = CtaFormStyle.toModifier().toAttrs {
                        attr("novalidate", "")

                        // The `Form` DSL exposes no `onSubmit`; this is what makes Enter submit.
                        ref { form ->
                            val listener: (org.w3c.dom.events.Event) -> Unit = { event ->
                                event.preventDefault()
                                submit()
                            }
                            form.addEventListener("submit", listener)
                            onDispose { form.removeEventListener("submit", listener) }
                        }
                    },
                ) {
                    TextInput(
                        text = draft,
                        onTextChange = { draft = it },
                        modifier = Modifier.attrsModifier {
                            attr("type", "email")
                            attr("aria-label", t.gate.emailLabel)
                            attr("autocomplete", "email")
                            if (error != null) attr("aria-invalid", "true")
                        },
                        variant = CtaInputVariant,
                        placeholder = t.gate.emailPlaceholder,
                        focusBorderColor = null,
                    )

                    Button(
                        onClick = { submit() },
                        modifier = Modifier.attrsModifier { attr("type", "submit") },
                        variant = PillPrimaryVariant,
                    ) {
                        Text(t.gate.submit)
                    }
                }

                // The border color cannot be the only thing that says the field is wrong.
                error?.let { message ->
                    Div(ErrorStyle.toModifier().toAttrs { attr("role", "alert") }) { Text(message) }
                }

                P(CtaNoteStyle.toModifier().toAttrs()) { Text(t.landing.ctaNote) }
            }
        }

        LandingFooter()
    }
}
